import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Select } from "@/shared/components/ui/select";
import {
  User,
  UserService,
  UpdateUserPayload,
} from "@/shared/services/user.service";
import {
  Organization,
  OrganizationService,
} from "@/shared/services/organization.service";
import { RoleService, Role } from "@/shared/services/role.service";
import { toast } from "sonner";

// Form schema with validation
const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: "User name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  role_id: z.string().min(1, { message: "Please select a role." }),
  organization_id: z
    .string()
    .min(1, { message: "Please select an organization." }),
});

type FormValues = z.infer<typeof formSchema>;

interface EditUserModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  isOpen,
  onClose,
  onUserUpdated,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      role_id: "",
      organization_id: "",
    },
  });

  // Load organizations and roles and set form values when modal opens
  useEffect(() => {
    if (isOpen && user) {
      const fetchData = async () => {
        try {
          const [orgsData, rolesData] = await Promise.all([
            OrganizationService.getAll(),
            RoleService.getAll(),
          ]);

          setOrganizations(orgsData);
          setRoles(rolesData);

          // Find the current role ID
          const currentRole = rolesData.find(
            (role) => role.role_name === user.role
          );

          // Set form values
          form.reset({
            name: user.name || "",
            email: user.email || "",
            role_id: currentRole?.id || "",
            organization_id: user.organization?.id || "",
          });
        } catch (err) {
          setError("Failed to load data");
          console.error("Error loading data:", err);
        }
      };
      fetchData();
    }
  }, [isOpen, user, form]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      form.reset();
      setError(null);
    }
  }, [isOpen, form]);

  const onSubmit = async (data: FormValues) => {
    if (!user) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const payload: UpdateUserPayload = {
        user_id: user.id,
        name: data.name,
        email: data.email,
        role_id: data.role_id,
        organization_id: data.organization_id,
      };

      await UserService.update(payload);
      toast.success("User updated successfully");
      onClose();
      onUserUpdated();
    } catch (err) {
      setError("Failed to update user");
      toast.error("Failed to update user");
      console.error("Error updating user:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit User: {user?.name}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>User Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter user name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Role</FormLabel>
                  <FormControl>
                    <Select {...field}>
                      <option value="" disabled>
                        Select a role
                      </option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="organization_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Organization</FormLabel>
                  <FormControl>
                    <Select {...field}>
                      <option value="" disabled>
                        Select an organization
                      </option>
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update User"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
